import { QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink } from '@trpc/client';
import { useState, type ReactNode } from 'react';
import superjson from 'superjson';
import { createQueryClient, trpc, type RouterOutputs } from '../../lib/trpc';
import { isFeatureOn } from '../../lib/features';
import { apiBase } from '../../lib/workspace';

/**
 * Client-portal plumbing. The portal talks to the API as the client of ONE workspace (the slug in
 * the URL), independent of the workspace a team member has selected in the main app, so it gets
 * its own tRPC client (fixed `x-workspace-slug`) and its own query cache.
 */
export function PortalTrpcProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);
  const [client] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: `${apiBase}/trpc`,
          transformer: superjson,
          headers: () => ({ 'x-workspace-slug': slug }),
          fetch: (url, options) => fetch(url, { ...(options as RequestInit), credentials: 'include' }),
        }),
      ],
    }),
  );
  return (
    <trpc.Provider client={client} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}

/** CSS variables that re-skin the design system's primary color with the workspace brand (shared with /c/<slug>). */
export { brandStyle } from '../../shell/brand';

export type PortalMe = RouterOutputs['portal']['client']['me']['get'];
export type PortalProjectHome = RouterOutputs['portal']['client']['projects']['get'];
export type PortalProjectSummary = RouterOutputs['portal']['client']['projects']['list'][number];
export type PortalPhoto = RouterOutputs['portal']['client']['projects']['photos']['items'][number];
export type PortalAlbum = RouterOutputs['portal']['client']['projects']['albums'][number];
export type PortalMeeting = RouterOutputs['portal']['client']['projects']['meetings'][number];
export type PortalSchedule = RouterOutputs['portal']['client']['projects']['schedule'];
export type PortalDocument = RouterOutputs['portal']['client']['projects']['documents'][number];
export type PortalSelection = RouterOutputs['portal']['client']['selections']['list'][number];
export type PortalChangeOrderRow = RouterOutputs['portal']['client']['changeOrders']['list'][number];
export type PortalChangeOrder = RouterOutputs['portal']['client']['changeOrders']['get'];
export type PortalInvoiceRow = RouterOutputs['portal']['client']['invoices']['list'][number];
export type PortalInvoice = RouterOutputs['portal']['client']['invoices']['get'];
export type PortalMessage = RouterOutputs['portal']['client']['messages']['list']['items'][number];

export const PORTAL_TABS = [
  { key: 'overview', label: 'Overview', capability: null },
  { key: 'photos', label: 'Photos', capability: 'view_photos' },
  { key: 'schedule', label: 'Schedule', capability: 'view_schedule' },
  { key: 'documents', label: 'Documents', capability: 'view_documents' },
  { key: 'meetings', label: 'Meetings', capability: 'view_progress' },
  { key: 'selections', label: 'Selections', capability: 'make_selections' },
  { key: 'change-orders', label: 'Change orders', capability: 'approve_change_orders' },
  { key: 'invoices', label: 'Invoices', capability: 'view_invoices' },
  { key: 'messages', label: 'Messages', capability: 'message' },
] as const;
export type PortalTab = (typeof PORTAL_TABS)[number]['key'];

/**
 * Portal tabs the client can open: their capabilities, minus Meetings while project meetings are
 * hidden for the first release (lib/features.ts).
 */
export function visiblePortalTabs(capabilities: readonly string[]) {
  return PORTAL_TABS.filter(
    (t) => (t.capability === null || capabilities.includes(t.capability)) && (t.key !== 'meetings' || isFeatureOn('project.meetings')),
  );
}

export function isPortalTab(v: unknown): v is PortalTab {
  return typeof v === 'string' && PORTAL_TABS.some((t) => t.key === v);
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const STATUS_VARIANTS: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'destructive' | 'info' | 'outline'> = {
  sent: 'info',
  viewed: 'info',
  partially_paid: 'warning',
  paid: 'success',
  overdue: 'destructive',
  approved: 'success',
  rejected: 'destructive',
  pending_client: 'warning',
  selected: 'info',
  ordered: 'primary',
  installed: 'success',
  draft: 'outline',
  processing: 'warning',
  succeeded: 'success',
  failed: 'destructive',
  active: 'success',
  planning: 'outline',
  preconstruction: 'info',
  on_hold: 'warning',
  punch: 'primary',
  completed: 'success',
  closed: 'default',
  cancelled: 'destructive',
  revoked: 'destructive',
  disabled: 'outline',
};

export function statusVariant(status: string) {
  return STATUS_VARIANTS[status] ?? 'default';
}

const STATUS_LABELS: Record<string, string> = {
  pending_client: 'Needs your choice',
  selected: 'Chosen',
  sent: 'Awaiting approval',
  partially_paid: 'Partially paid',
  rejected: 'Declined',
};

/** Client-facing wording for a status (falls back to Title Case). */
export function statusLabel(status: string, context: 'change_order' | 'invoice' | 'selection' | 'other' = 'other'): string {
  if (context === 'invoice' && status === 'sent') return 'Due';
  if (context === 'invoice' && status === 'viewed') return 'Due';
  if (context !== 'change_order' && status === 'sent') return 'Sent';
  const label = STATUS_LABELS[status];
  if (label) return label;
  return status.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}
